import { useQuery } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import loadInfluencerWinRates from '../../actions/tips/loadInfluencerWinRates';
import { generalErrorHandler } from '../../actions/utils';
import FlatSelect from '../../components/form/FlatSelect';
import HeadOverride from '../../components/general/HeadOverride';
import HoverableSpan from '../../components/general/HoverableSpan';
import {
  returnTillNowColumn,
  winPercentageColumn,
} from '../../components/table/columns';
import DateTimeCell, { DateType } from '../../components/table/DateTimeCell';
import DLStockPeek from '../../components/table/DLStockPeek';
import DLTable from '../../components/table/DLTable/DLTable';
import { StringFilter } from '../../components/table/filters';
import StockCodeCell from '../../components/table/StockCodeCell';
import CreateOrEditTipsModal, {
  CurEditTipsData,
} from '../../components/tips/CreateOrEditTipsModal';
import CreateTipsButton from '../../components/tips/CreateTipsButton';
import WinPercentageModal from '../../components/tips/InfluencerWinPercentageModal';
import { usePageVisitAnalytics } from '../../helpers/analytics.helper';
import { tipsHelpers } from '../../helpers/index';
import { bsTiny } from '../../helpers/ui.helper';
import { useNavigateCCASS } from '../../helpers/ui.helper';
import useDispatch from '../../hooks/useDispatch';
import useRegion from '../../hooks/useRegion';
import { useUser } from '../../hooks/users';
import { TipsModel } from '../../reducers/tips.reducer';
import { tipsService } from '../../services';
import { TipsStatus } from '../../shared/types';
import { ReactTable } from '../../types';
import BlurPlaceholder from '../FreeUser/BlurPlaceholder';
import PurchaseVIPFloatButton from '../FreeUser/PurchaseVIPFloatButton';
import TutorialQuestionMark from '../Help/TutorialQuestionMark';

export const InfluencerCell = styled.div`
  cursor: pointer;
  &:hover {
    font-weight: 600;
    opacity: 0.8;
  }
`;

export const renderIntervalFilter = (
  intervals: string[],
  t: TFunction,
  setWinInterval: (interval: string) => void,
  winPercentageInterval: string
): JSX.Element => {
  return (
    <FlatSelect
      options={intervals}
      labels={intervals.map(interval => t(interval))}
      onValueChange={setWinInterval}
      value={winPercentageInterval}
    />
  );
};

export const influencerTipsCols = (
  t: TFunction,
  winPercentageInterval: string,
  intervalFilter: JSX.Element,
  onClickInfluencerCell: (influencerId: number, name: string) => void,
  sticky?: boolean
): ReactTable.Column[] => {
  return [
    {
      Header: t('influencer'),
      id: 'tips-influencer',
      accessor: 'name',
      width: 100,
      contentAlign: 'left',
      Filter: StringFilter,
      filter: 'text',
      sticky: sticky ? 'left' : undefined,
      Cell: ({ cell: { value }, row: { original } }) => (
        <InfluencerCell
          onClick={() =>
            onClickInfluencerCell(original.influencerId, original.name)
          }
        >
          {value}
        </InfluencerCell>
      ),
    },
    {
      Header: t('stock-code'),
      id: 'tips-wp-stockCode',
      className: 'stock-code-cell',
      width: 100,
      accessor: 'stockCode',
      exportAccessor: 'stockCode',
      Filter: StringFilter,
      filter: 'text',
      Cell: (CellProp: ReactTable.CellProps) =>
        StockCodeCell({ ...CellProp, showPlaneValue: true }),
    },
    {
      Header: t('direction'),
      id: 'tips-wp-direction',
      accessor: 'direction',
      width: 100,
      Filter: StringFilter,
      filter: 'text',
      Cell: ({ cell: { value } }) => (
        <div style={{ color: value === 'bullish' ? 'green' : 'red' }}>
          {t(`tips-${value}`)}
        </div>
      ),
    },
    {
      Header: t('tips-price'),
      id: 'tips-wp-price',
      accessor: 'price',
      width: 100,
      disableFilters: true,
    },
    winPercentageColumn(t, winPercentageInterval, intervalFilter),
    returnTillNowColumn(t),
    {
      Header: t('key-comment'),
      id: 'tips-wp-keyComment',
      accessor: 'keyComment',
      width: 400,
      disableFilters: true,
      contentAlign: 'left',
      Cell: HoverableSpan,
    },
    {
      Header: t('tips-date'),
      id: 'tips-wp-date',
      accessor: 'date',
      width: 200,
      Filter: StringFilter,
      filter: 'text',
      Cell: ({ cell }) => (
        <DateTimeCell cell={cell} dateType={DateType.local} />
      ),
    },
    {
      Header: t('created-at'),
      id: 'tips-wp-createdAt',
      accessor: 'createdAt',
      width: 200,
      Filter: StringFilter,
      filter: 'text',
      Cell: ({ cell }) => (
        <DateTimeCell cell={cell} dateType={DateType.local} />
      ),
    },
    {
      Header: t('channel'),
      id: 'tips-wp-channel',
      accessor: 'channel',
      width: 100,
      disableFilters: true,
    },
    {
      Header: t('source-link'),
      id: 'tips-wp-sourceLink',
      accessor: 'sourceLink',
      width: 200,
      disableFilters: true,
      contentAlign: 'left',
      Cell: ({ cell: { value } }) => (
        <a href={value} target="_blank" rel="noreferrer">
          {value}
        </a>
      ),
    },
    {
      Header: t('created-by'),
      id: 'tips-tipsCreatedBy',
      accessor: (item: TipsModel) => item.publicAlias,
      width: 100,
      Filter: StringFilter,
      filter: 'text',
    },
  ];
};

const intervals = ['1d', '3d', '5d', '1m', '3m', '1y'];

const DoubtButton: React.FC<{
  t: TFunction;
  cell: ReactTable.Cell;
  fetchTips: () => void;
}> = ({ t, cell, fetchTips }) => {
  const dispatch = useDispatch();
  const [doubtLoading, setDoubtLoading] = useState<boolean>(false);
  const { status } = cell.row.original;

  const doubtTip = async () => {
    try {
      setDoubtLoading(true);
      const { id } = cell.row.original;
      await tipsService.doubtTip(id);
      fetchTips();
    } catch (error) {
      generalErrorHandler(dispatch)(error);
    } finally {
      setDoubtLoading(false);
    }
  };

  const disable = doubtLoading || status === TipsStatus.verified;

  return disable ? null : (
    <Button
      style={{ marginLeft: 5, color: '#B22222', borderColor: '#B22222' }}
      variant="outline-secondary"
      size={bsTiny}
      onClick={doubtTip}
      disabled={disable}
    >
      {t('doubt-tips-button')}
    </Button>
  );
};

const VerifyButton: React.FC<{
  t: TFunction;
  cell: ReactTable.Cell;
  fetchTips: () => void;
  type: 'verify' | 'delete';
}> = ({ t, cell, fetchTips, type }) => {
  const dispatch = useDispatch();
  const [verifyLoading, setVerifyLoading] = useState<boolean>(false);
  const { status } = cell.row.original;

  const verifyTip = async () => {
    try {
      await setVerifyLoading(true);
      const { id } = cell.row.original;
      await tipsService.verifyTip(id, type === 'verify');
      fetchTips();
    } catch (error) {
      generalErrorHandler(dispatch)(error);
    } finally {
      setVerifyLoading(false);
    }
  };
  const verifyStyles = { color: 'green', borderColor: 'green' };
  const deleteStyles = { color: 'red', borderColor: 'red' };
  const colorStyles = type === 'verify' ? verifyStyles : deleteStyles;

  const disable =
    verifyLoading ||
    status === TipsStatus.verified ||
    status === TipsStatus.deleted;

  return disable ? null : (
    <Button
      style={{ marginLeft: 5, ...colorStyles }}
      variant="outline-secondary"
      size={bsTiny}
      onClick={verifyTip}
      disabled={disable}
    >
      {type === 'verify' ? t('verify-tips-button') : t('delete-tips-button')}
    </Button>
  );
};

const EDIT_COLUMN_ID = 'tips-editTipsButton';
const VERIFY_COLUMN_ID = 'tips-verityButtons';

export const TipsTable: React.FC = () => {
  const {
    isLoading: loading,
    data: tips,
    refetch: fetchTips,
  } = useQuery(['fetchTips'], () => tipsService.getTips());

  const user = useUser();
  const region = useRegion();

  const isAdmin = user && user.isAdmin;
  const [isShowCreateModal, setCreateModalState] = useState<boolean>(false);
  const [isShowWinModal, setWinModalState] = useState<boolean>(false);
  const [curEditingTips, setEditingTips] = useState<CurEditTipsData>();
  const [winPercentageInterval, setWinInterval] = useState<string>('1d');
  const [winPercentageInfluencerName, setWinPercentageInfluencerName] =
    useState<string>('');

  const dispatch = useDispatch();

  const [t] = useTranslation();
  const navigateCCASS = useNavigateCCASS();

  let columns: ReactTable.Column[] = useMemo(
    () => [
      ...influencerTipsCols(
        t,
        winPercentageInterval,
        renderIntervalFilter(
          intervals,
          t,
          setWinInterval,
          winPercentageInterval
        ),
        (influencerId, name) => {
          setWinModalState(true);
          dispatch(loadInfluencerWinRates(region, influencerId));
          setWinPercentageInfluencerName(name);
        },
        true
      ),
      {
        Header: t('tips-status'),
        id: 'tips-tipsStatus',
        accessor: (item: TipsModel) => {
          return tipsHelpers.statusMapper(t, item.status);
        },
        width: 100,
        disableFilters: true,
      },
      {
        Header: t('doubt-tips-button'),
        id: 'tips-doubtTipsButton',
        Cell: ({ cell }) => {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flex: 1,
              }}
            >
              <DoubtButton t={t} cell={cell} fetchTips={fetchTips} />
            </div>
          );
        },
        width: 60,
        disableFilters: true,
      },
      {
        Header: t('edit-tips-button'),
        id: EDIT_COLUMN_ID,
        Cell: ({ cell }) => {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flex: 1,
              }}
            >
              <Button
                variant="outline-secondary"
                size={bsTiny}
                onClick={() => {
                  const {
                    id,
                    name,
                    stockCode,
                    direction,
                    date,
                    channel,
                    sourceLink,
                    keyComment,
                  } = cell.row.original;

                  setEditingTips({
                    tipsId: id,
                    influencer: name,
                    stockCode,
                    direction,
                    date: new Date(date),
                    channel,
                    sourceLink,
                    keyComment,
                  });
                  setCreateModalState(true);
                }}
              >
                {t('edit-tips-button')}
              </Button>
            </div>
          );
        },
        width: 60,
        disableFilters: true,
      },
      {
        Header: t('tips-verify'),
        id: VERIFY_COLUMN_ID,
        Cell: ({ cell }) => {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flex: 1,
              }}
            >
              <>
                <VerifyButton
                  t={t}
                  cell={cell}
                  fetchTips={fetchTips}
                  type="verify"
                />
                <VerifyButton
                  t={t}
                  cell={cell}
                  fetchTips={fetchTips}
                  type="delete"
                />
              </>
            </div>
          );
        },
        width: 100,
        disableFilters: true,
      },
    ],
    [t, dispatch, region, winPercentageInterval, fetchTips]
  );

  if (!isAdmin) {
    columns = columns.filter(
      item => item.id !== VERIFY_COLUMN_ID && item.id !== EDIT_COLUMN_ID
    );
  }

  const onHideCreateModal = useCallback(() => {
    setCreateModalState(false);
    setEditingTips(null);
  }, []);

  const onHideWinModal = useCallback(() => {
    setWinModalState(false);
  }, []);

  return (
    <div>
      <WinPercentageModal
        show={isShowWinModal}
        onHide={onHideWinModal}
        influencerName={winPercentageInfluencerName}
      />
      <CreateOrEditTipsModal
        show={isShowCreateModal}
        onHide={onHideCreateModal}
        onSubmit={fetchTips}
        initialValues={curEditingTips}
      />
      <Row style={{ justifyContent: 'center' }}>
        <Col
          md={12}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CreateTipsButton onClick={() => setCreateModalState(true)} />
        </Col>
        <Col
          md={12}
          xs={8}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: 12,
            marginBottom: 20,
          }}
        >
          <div style={{ marginRight: 5 }}>
            {t('tips-create-tips-principle')}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <TutorialQuestionMark tutorialId={35} alwaysVisible />
          </div>
        </Col>
        <Col md={12}>
          <DLTable
            tableId="tips"
            columns={columns}
            data={tips || []}
            loading={loading}
            defaultPageSize={20}
            renderRowSubComponent={({
              mode,
              row: {
                original: { stockCode, market, influencerId },
              },
            }) => {
              return (
                <DLStockPeek
                  stockCode={stockCode}
                  mode={mode}
                  onNavigateCCASS={() => navigateCCASS(stockCode, new Date())}
                  region={market}
                  influencerId={influencerId}
                />
              );
            }}
          />
        </Col>
      </Row>
    </div>
  );
};

const TipsPage: React.FC = () => {
  usePageVisitAnalytics();
  const user = useUser();
  return (
    // Override default padding so sticky column will not get cropped
    <Container
      fluid={Boolean(user)}
      style={{ paddingLeft: 15, paddingRight: 15 }}
    >
      <HeadOverride>Tips</HeadOverride>
      {!user && <PurchaseVIPFloatButton />}
      <BlurPlaceholder featureKey={'tips'} showContent={Boolean(user)}>
        <TipsTable />
      </BlurPlaceholder>
    </Container>
  );
};

export default React.memo(TipsPage);
